import React, { useState } from 'react';
import styles from './Races.module.css';

const RACES = [
  {
    name: 'Human',
    color: '#C8A75B',
    intro: 'Humans are the most versatile race with balanced stats. They can fill any role on the battlefield, making them the ideal choice for new players and veterans alike.',
    sections: [
      {
        label: 'Warriors / Melee',
        classes: ['Warrior', 'Warlord', 'Gladiator', 'Paladin', 'Dark Avenger'],
      },
      {
        label: 'Rogue / Archer',
        classes: ['Rogue', 'Treasure Hunter', 'Hawkeye'],
      },
      {
        label: 'Wizards',
        classes: ['Wizard', 'Sorcerer', 'Necromancer', 'Warlock'],
      },
      {
        label: 'Support / Healer',
        classes: ['Cleric', 'Bishop', 'Prophet'],
      },
    ],
  },
  {
    name: 'Elf',
    color: '#4A8C5C',
    intro: 'Elves specialize in speed, accuracy, and magic. Their deep connection to nature grants them unparalleled skill with the bow and powerful support abilities.',
    sections: [
      {
        label: 'Warriors',
        classes: ['Elven Knight', 'Temple Knight', 'Swordsinger'],
      },
      {
        label: 'Rogue / Archer',
        classes: ['Elven Scout', 'Plainswalker', 'Silver Ranger'],
      },
      {
        label: 'Wizards',
        classes: ['Elven Wizard', 'Spellsinger'],
      },
      {
        label: 'Summoner',
        classes: ['Elemental Summoner'],
      },
      {
        label: 'Support / Healer',
        classes: ['Elven Oracle', 'Elven Elder'],
      },
    ],
  },
  {
    name: 'Dark Elf',
    color: '#7B5EA7',
    intro: 'Dark Elves focus on high damage and dark magic. Exiled from elven society, they turned their pain into power, becoming some of the most feared combatants in Aden.',
    sections: [
      {
        label: 'Warriors',
        classes: ['Palus Knight', 'Shillien Knight', 'Bladedancer'],
      },
      {
        label: 'Rogue / Assassin',
        classes: ['Assassin', 'Abyss Walker', 'Phantom Ranger'],
      },
      {
        label: 'Wizards',
        classes: ['Dark Wizard', 'Spellhowler'],
      },
      {
        label: 'Summoner',
        classes: ['Phantom Summoner'],
      },
      {
        label: 'Support',
        classes: ['Shillien Oracle', 'Shillien Elder'],
      },
    ],
  },
  {
    name: 'Orc',
    color: '#8B3A2D',
    intro: 'Orcs have high HP and unmatched physical strength. Whether smashing through enemy lines as warriors or empowering their allies as shamans, Orcs are the backbone of any siege.',
    sections: [
      {
        label: 'Warriors',
        classes: ['Orc Raider', 'Destroyer', 'Monk', 'Tyrant'],
      },
      {
        label: 'Shamans',
        classes: ['Orc Shaman', 'Overlord', 'Warcryer'],
      },
    ],
  },
  {
    name: 'Dwarf',
    color: '#8B6B2D',
    intro: 'Dwarves are the crafting and economy specialists of Aden. Their crafted weapons and armor equip armies, and their wealth funds entire wars.',
    sections: [
      {
        label: 'Warriors',
        classes: ['Dwarf Fighter', 'Scavenger', 'Bounty Hunter'],
      },
      {
        label: 'Crafter',
        classes: ['Artisan', 'Warsmith'],
      },
    ],
  },
  {
    name: 'Kamael',
    color: '#5B7EC8',
    intro: 'A unique race with gender-restricted classes and soul weapons. Kamaels are born with a single dark wing and grow more powerful by consuming the souls of their fallen enemies.',
    note: 'Gender-restricted classes',
    sections: [
      {
        label: 'Male Kamael',
        classes: ['Berserker', 'Soul Breaker (male)', 'Doombringer'],
      },
      {
        label: 'Female Kamael',
        classes: ['Arbalester', 'Soul Breaker (female)', 'Trickster'],
      },
      {
        label: 'Shared',
        classes: ['Inspector', 'Judicator'],
      },
    ],
  },
  {
    name: 'Ertheia',
    color: '#B07EC8',
    intro: 'Ertheia are female-only classes with entirely unique mechanics. Born from pure creation energy, their three paths each offer a playstyle found nowhere else in Aden.',
    note: 'Female only',
    sections: [
      {
        label: 'Classes',
        classes: ['Sayha Seer', 'Etherial Knight', 'Valiant'],
      },
    ],
  },
  {
    name: 'High Elf',
    color: '#7BA7C8',
    intro: 'High Elves were introduced in the Essence era with two exclusive classes. A noble bloodline offering a focused choice between martial and arcane excellence.',
    note: 'Essence era addition',
    sections: [
      {
        label: 'Classes',
        classes: ['Divine Templar (knight style)', 'Element Weaver (wizard style)'],
      },
    ],
  },
];

export default function Races() {
  const [active, setActive] = useState(null);

  const toggle = (name) => setActive(prev => (prev === name ? null : name));

  return (
    <main className={styles.main}>
      <section className="page-hero" aria-label="Races hero">
        <h1>Races &amp; Classes</h1>
        <p className={styles.heroSub}>
          Every race. Every class path. Choose your destiny in the world of Aden.
        </p>
      </section>

      <div className={styles.container}>
        <p className={styles.intro}>
          Lineage II Guardians features eight distinct races, each with their own class trees, playstyles, and lore. Whether you want to forge weapons, lead sieges, or dominate in the Olympiad - your path starts here.
        </p>

        <div className={styles.raceList}>
          {RACES.map(({ name, color, intro, note, sections }) => {
            const isOpen = active === name;
            return (
              <div
                key={name}
                className={`${styles.raceCard} ${isOpen ? styles.open : ''}`}
                style={{ '--race-color': color }}
              >
                <button
                  className={styles.raceHeader}
                  onClick={() => toggle(name)}
                  aria-expanded={isOpen}
                  aria-controls={`race-${name}`}
                >
                  <div className={styles.raceGlyph} aria-hidden="true" />
                  <div className={styles.raceTitleGroup}>
                    <span className={styles.raceName}>{name}</span>
                    {note && <span className={styles.raceNote}>{note}</span>}
                  </div>
                  <span className={styles.chevron} aria-hidden="true">{isOpen ? '-' : '+'}</span>
                </button>

                {isOpen && (
                  <div id={`race-${name}`} className={styles.raceBody}>
                    <p className={styles.raceIntro}>{intro}</p>
                    <div className={styles.sectionsGrid}>
                      {sections.map(({ label, classes, accent }) => (
                        <div key={label} className={`${styles.classSection} ${accent ? styles.accentSection : ''}`}>
                          <h3 className={styles.sectionLabel}>{label}</h3>
                          <ul className={styles.classList}>
                            {classes.map(c => <li key={c}>{c}</li>)}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}
